/**
 * Custom-domain verification-error classification (pure).
 *
 * Cloudflare reports verification/SSL errors that are EXPECTED while DNS is still
 * propagating and self-heal once it lands. Surfacing them as a config alarm
 * false-alarms the happy path — verified on Hostinger 2026-06-15: "custom hostname
 * does not CNAME to this zone" + "is using Cloudflare … cannot be activated with a
 * TXT or HTTP validation token" both cleared once propagation completed.
 *
 * So instead of dumping raw CF strings, the card CLASSIFIES: transient → calm note
 * after a long grace; anything else → an actionable note pointing at the DNS-records
 * list. Kept here (not in the component) so it's pure + unit-testable.
 */

/** CF error substrings that are propagation-transient and self-heal. */
export const TRANSIENT_CF_PATTERNS: readonly RegExp[] = [
  /does not CNAME to this zone/i,
  /is using Cloudflare/i,
  /pending/i,
  /acme/i, // _acme-challenge TXT not yet visible (legacy TXT DCV)
  /validation.*(pending|in progress)|http validation/i, // HTTP DCV still validating
];

export type VerificationClass = "none" | "propagating" | "actionable";

export function classifyVerification(
  errors: string[] | undefined,
): VerificationClass {
  if (!errors?.length) return "none";
  return errors.every((e) => TRANSIENT_CF_PATTERNS.some((p) => p.test(e)))
    ? "propagating"
    : "actionable";
}

// Don't nag during normal propagation: only show the calm "still waiting" line
// after a long grace (slow registrars like Hostinger take ~30–60 min). Genuine
// config errors (non-transient) surface sooner.
export const PROPAGATING_GRACE_MS = 30 * 60_000;
export const ACTIONABLE_GRACE_MS = 5 * 60_000;
